import { useTranslation } from '@web_opxp/features';
import { FC } from 'react';
import { IDomainWeight } from '../../base/types';
import './DomainPieChart.scss';

export interface IDomainPieChartProps {
  domains: IDomainWeight[];
}

function convertPercentToRad(percent: number) {
  return (percent * Math.PI * 2) / 100;
}

const PIE_CHART_SIZE = 10;

export const DomainPieChart: FC<IDomainPieChartProps> = ({ domains }) => {
  const { t } = useTranslation();

  const r = PIE_CHART_SIZE;
  const circumference = Math.PI * r;
  const width = r * 4;
  const height = r * 2;
  const centerX = width / 2;
  const centerY = height / 2;
  // we need to accumulate all previous weights to rotate next sector around
  let cumulativeWeight = 0;
  const domainSectors = domains.map((domain, index) => {
    // length of the current sector filled stroke
    const strokeLength = (domain.weight * circumference) / 100;
    // length of the empty stroke
    const spaceLength = circumference - strokeLength;
    // weight of the previous sector
    const prevWeight = index > 0 ? domains[index - 1].weight : 0;
    cumulativeWeight = cumulativeWeight + prevWeight;
    // length of the previous sectors
    const prevLength = (cumulativeWeight * circumference) / 100;
    // angle to the sector label in radians, rotation is clockwise from X axis ->
    const textAngle = convertPercentToRad(domain.weight) / 2 + convertPercentToRad(cumulativeWeight) - Math.PI / 2;
    // position of the label -PI/2 = rotate -90 deg same as circle (to align with Y axis)
    const textX = centerX + Math.cos(textAngle) * (r + 3);
    const textY = centerY + Math.sin(textAngle) * (r + 3);
    // change alignment if text is on the left side of the
    let textAnchor: string;
    if (Math.abs(textAngle) === Math.PI / 2) {
      textAnchor = 'middle';
    } else if (textAngle > Math.PI / 2) {
      textAnchor = 'end';
    } else {
      textAnchor = 'start';
    }

    const domainTitle = t(`common:Domain.Title.${domain.id}`);
    // forced unconditional linebreak for long labels
    const renderLines = domainTitle.split(' ').map((word, index) => (
      <tspan x={textX} dy={index === 0 ? 0 : 3} key={index}>
        {word}
      </tspan>
    ));
    return (
      <g key={domain.id} className={`domain-${domain.id}`}>
        <circle
          r={r / 2}
          cx={centerX}
          cy={centerY}
          strokeWidth={r}
          strokeDasharray={`${strokeLength} ${spaceLength}`}
          strokeDashoffset={`-${prevLength}`}
          transform={`rotate(-90, ${centerX}, ${centerY})`}
        />
        <text x={textX} y={textY} textAnchor={textAnchor}>
          {renderLines}
        </text>
      </g>
    );
  });
  return (
    <svg viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg" className="domain-pie-chart">
      {domainSectors}
    </svg>
  );
};

export default DomainPieChart;
